import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { chipTextColor } from '../../utils/format';
import { computeReapplyDiff } from '../../utils/computeReapplyDiff';
import { useReapplyProjectTemplate } from '../../hooks/useProjectStates';
import {
  isStateInUseError,
  type ReapplyConflict,
  type TaskState,
} from '../../services/api/projectStates';
import type { ProjectTemplateState } from '../../services/api/projectTemplates';

export interface ReapplyTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  projectName: string;
  templateId: number;
  templateName: string;
  projectStates: TaskState[];
  templateStates: ProjectTemplateState[];
  /** Called when the re-apply succeeds (200). Parent surfaces AppToast. */
  onSuccess: (message: string) => void;
}

/**
 * Re-apply Template diff modal (Phase 20 PROJ-04 / D-15..D-19).
 *
 * Three vertically-stacked sections — copy aligned with the Phase 18 D-01
 * backend contract: re-apply is **additive**. The backend never deletes
 * project-only states (it leaves zero-task orphans intact and returns 409
 * STATE_IN_USE if a project-only state has tasks). Earlier copy promised
 * deletion, which contradicted observed behavior.
 *
 *   - Will be added: states in the template missing from the project (by name match).
 *   - Project-only states: states only on the project — kept as-is, blocked by 409 if they have tasks.
 *   - Will be updated: states matched by name — color/ordering may change.
 *
 * Diff is computed client-side via computeReapplyDiff (D-17).
 *
 * On confirm → POST /admin/projects/:projectId/reapply-template (D-18).
 * On 409 STATE_IN_USE (D-19): modal STAYS OPEN, conflicts are mapped onto
 * the matching project-only chips with task-count badge + `View tasks →` link.
 */
export function ReapplyTemplateModal({
  isOpen,
  onClose,
  projectId,
  projectName,
  templateId,
  templateName,
  projectStates,
  templateStates,
  onSuccess,
}: ReapplyTemplateModalProps) {
  const reapplyMutation = useReapplyProjectTemplate(projectId);
  const [conflicts, setConflicts] = useState<ReapplyConflict[]>([]);
  const [genericError, setGenericError] = useState<string | null>(null);

  const diff = useMemo(
    () => computeReapplyDiff(projectStates, templateStates),
    [projectStates, templateStates]
  );

  // Map stateName → conflict for fast lookup in the "Will be removed" section.
  const conflictByName = useMemo(() => {
    const map = new Map<string, ReapplyConflict>();
    for (const c of conflicts) map.set(c.stateName, c);
    return map;
  }, [conflicts]);

  // Reset error state when reopening OR when the parent swaps templates
  // without unmounting the modal. WR-07: previously the reset was only
  // gated on `isOpen` flipping false→true, so a parent that just changed
  // `templateId` would keep stale conflicts/error visible.
  useEffect(() => {
    if (isOpen) {
      setConflicts([]);
      setGenericError(null);
    }
  }, [isOpen, templateId]);

  function handleConfirm() {
    // Clear stale conflicts so the visible state during the next attempt is clean.
    setConflicts([]);
    setGenericError(null);
    reapplyMutation.mutate(templateId, {
      onSuccess: () => {
        onSuccess('Template applied');
        onClose();
      },
      onError: (err) => {
        if (isStateInUseError(err)) {
          setConflicts(err.details.conflicts);
        } else {
          setGenericError(
            err instanceof Error ? err.message : 'Failed to apply template — please retry.'
          );
        }
      },
    });
  }

  const renderChip = (name: string, color: string, extra?: React.ReactNode) => (
    <div className="inline-flex items-center mr-2 mb-2" key={name}>
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: color, color: chipTextColor(color) }}
      >
        {name}
      </span>
      {extra}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Re-apply "${templateName}" to ${projectName}?`}
      size="2xl"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={reapplyMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} isLoading={reapplyMutation.isPending}>
            Re-apply
          </Button>
        </div>
      }
    >
      <div className="space-y-6 text-sm" data-testid="reapply-modal-body">
        {/* Will be added */}
        <section data-section="added">
          <h3 className="font-semibold text-green-700 mb-1">Will be added</h3>
          <p className="text-xs text-gray-500 mb-2">
            These states will be created on this project.
          </p>
          {diff.added.length === 0 ? (
            <p className="text-xs italic text-gray-400">Nothing to add.</p>
          ) : (
            <div className="flex flex-wrap">
              {diff.added.map((s) => renderChip(s.name, s.color))}
            </div>
          )}
        </section>

        {/* Will be updated (matched by name) */}
        <section data-section="kept">
          <h3 className="font-semibold text-gray-700 mb-1">Will be updated</h3>
          <p className="text-xs text-gray-500 mb-2">
            These states are present in both — they survive (tasks keep their assignments) but their
            color and ordering will be updated to match the template.
          </p>
          {diff.kept.length === 0 ? (
            <p className="text-xs italic text-gray-400">Nothing to update.</p>
          ) : (
            <div className="flex flex-wrap">
              {diff.kept.map((s) => renderChip(s.name, s.color))}
            </div>
          )}
        </section>

        {/* Project-only — kept as-is, blocked by 409 if any has tasks */}
        <section data-section="removed">
          <h3 className="font-semibold text-gray-700 mb-1">Only on this project</h3>
          <p className="text-xs text-gray-500 mb-2">
            These states are not in the template. Re-apply leaves them untouched. If any of them
            still has tasks, the server returns 409 STATE_IN_USE and re-apply is blocked — move
            those tasks to another state first.
          </p>
          {diff.removed.length === 0 ? (
            <p className="text-xs italic text-gray-400">No project-only states.</p>
          ) : (
            <div className="flex flex-wrap">
              {diff.removed.map((s) => {
                const conflict = conflictByName.get(s.name);
                const extra = conflict ? (
                  <span className="ml-2 inline-flex items-center gap-2 text-xs text-red-700">
                    <span className="font-medium">
                      {conflict.taskCount} {conflict.taskCount === 1 ? 'task' : 'tasks'} blocking
                    </span>
                    <Link
                      to={`/manager/tasks?projectId=${projectId}&stateId=${conflict.stateId}`}
                      className="underline hover:text-red-900"
                    >
                      View tasks →
                    </Link>
                  </span>
                ) : null;
                return renderChip(s.name, s.color, extra);
              })}
            </div>
          )}
        </section>

        {genericError && (
          <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {genericError}
          </p>
        )}
      </div>
    </Modal>
  );
}
